import { DEFAULT_BRUSH_FALLOFF } from './brushFalloff';
import { MM_TO_SCENE, SCULPT_BRUSH, clamp01 } from './brushSettings';

/**
 * The contract between the Sculpt Smooth card and the smooth kernels.
 *
 * Same job as `brushSettings.js` does for the inflate brush: the card speaks
 * millimetres and percentages, because that is what a clinician reasons about;
 * the kernels speak scene units and unit factors, because that is what the maths
 * needs. Doing the conversion here — once, in a named place — is what keeps the
 * two vocabularies from leaking into each other.
 */

/**
 * UI-facing defaults and slider bounds, in millimetres / percent.
 *
 * Sized for cleaning up a raw limb scan rather than for making a clinical
 * correction: a 20 mm brush covers a scanner artefact in one pass, and 35%
 * strength is roughly one full smoothing pass per dab — firm enough to see the
 * noise go, gentle enough that a slow drag does not melt the anatomy under it.
 */
export const SMOOTH_BRUSH_LIMITS = {
  size: { min: 2, max: 60, step: 1, default: 20 },       // mm, radius
  strength: { min: 1, max: 100, step: 1, default: 35 },  // %
};

/**
 * Per-dab share of the detail direction that Enhance Details applies at 100%
 * strength.
 *
 * Sharpening is far less forgiving than smoothing — the artefacts it creates
 * cannot be smoothed back out without losing the detail that was there to begin
 * with — so a full-strength dab commits a fifth of the per-stroke cap rather
 * than reaching it in one touch.
 */
const ENHANCE_DAB_FRACTION = 0.2;

/**
 * @typedef {Object} SmoothBrushSettings
 * @property {'smooth'} brushType
 * @property {boolean} invert       - true = Enhance Details (Ctrl-drag)
 * @property {number} radius        - brush radius, scene units (world space)
 * @property {number} strength      - 0..1
 * @property {string} falloff       - a `BRUSH_FALLOFF` id
 * @property {boolean} pinBoundary  - leave open-boundary (trimline) vertices alone
 * @property {Uint8Array | null} [mask] - optional per-vertex protection, 0..255
 */

/**
 * Convert the tool card's state into kernel-ready {@link SmoothBrushSettings}.
 *
 * @param {Object} ui
 * @param {number} [ui.size]      - brush radius in mm
 * @param {number} [ui.strength]  - 1..100
 * @param {string} [ui.falloff]
 * @param {boolean} [ui.pinBoundary=true]
 * @param {Uint8Array | null} [ui.mask]
 * @param {boolean} [ui.invert=false] - Ctrl-drag: sharpen instead of smooth
 * @returns {SmoothBrushSettings}
 */
export function toSmoothBrushSettings({
  size = SMOOTH_BRUSH_LIMITS.size.default,
  strength = SMOOTH_BRUSH_LIMITS.strength.default,
  falloff = DEFAULT_BRUSH_FALLOFF,
  pinBoundary = true,
  mask = null,
  invert = false,
}) {
  const normalizedStrength = clamp01(strength / 100);

  return {
    brushType: SCULPT_BRUSH.SMOOTH,
    invert,
    radius: Math.max(size, 0.01) * MM_TO_SCENE,
    strength: invert ? normalizedStrength * ENHANCE_DAB_FRACTION : normalizedStrength,
    // Unknown ids are tolerated: `getFalloffFn` falls back to the default curve.
    falloff: falloff || DEFAULT_BRUSH_FALLOFF,
    pinBoundary,
    mask,
  };
}
